import { createContext, useContext, useEffect, useState, useCallback, useMemo, type ReactNode } from "react";
import type { ContentBundle, MediaBundle, MediaAsset, Locale } from "@/lib/content.functions";

export type Lang = Locale;

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  availableLocales: Lang[];
  /** Legacy inline translator. Kept while we migrate to key-based content. */
  t: (es: string, pt: string, en?: string) => string;
  /**
   * Key-based translator backed by the CMS content bundle.
   * Returns `fallback` (or the key itself) when the key isn't set yet.
   */
  tk: (key: string, fallback?: string) => string;
  /** Media asset lookup. Returns `undefined` if the key isn't set in the CMS. */
  media: (key: string) => MediaAsset | undefined;
};

const LangCtx = createContext<Ctx | null>(null);

const STORAGE_KEY = "rrlex.lang";

function detectInitial(): Lang {
  if (typeof window === "undefined") return "es";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "es" || stored === "pt" || stored === "en") return stored;
    const nav = navigator.language?.toLowerCase() ?? "";
    if (nav.startsWith("pt")) return "pt";
    if (nav.startsWith("en")) return "en";
  } catch {}
  return "es";
}

const EMPTY_CONTENT: ContentBundle = { es: {}, pt: {}, en: {} };

export function LanguageProvider({
  children,
  content = EMPTY_CONTENT,
  mediaBundle = {},
  availableLocales = ["es", "pt", "en"],
}: {
  children: ReactNode;
  content?: ContentBundle;
  mediaBundle?: MediaBundle;
  availableLocales?: Lang[];
}) {
  const [lang, setLangState] = useState<Lang>("es");

  useEffect(() => {
    const initial = detectInitial();
    setLangState(availableLocales.includes(initial) ? initial : (availableLocales[0] ?? "es"));
  }, [availableLocales]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {}
    setLangState(l);
  }, []);

  const t = useCallback(
    (es: string, pt: string, en?: string) => {
      if (lang === "pt") return pt;
      if (lang === "en") return en ?? es;
      return es;
    },
    [lang],
  );

  const tk = useCallback(
    (key: string, fallback?: string) => {
      const localized = content[lang]?.[key];
      if (localized) return localized;
      // Fall back across locales in priority order, then to the developer-provided fallback.
      return content.es?.[key] ?? content.en?.[key] ?? content.pt?.[key] ?? fallback ?? key;
    },
    [content, lang],
  );

  const media = useCallback((key: string) => mediaBundle[key], [mediaBundle]);

  const value = useMemo<Ctx>(
    () => ({ lang, setLang, availableLocales, t, tk, media }),
    [lang, setLang, availableLocales, t, tk, media],
  );

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useT() {
  const ctx = useContext(LangCtx);
  if (!ctx) {
    return {
      lang: "es" as Lang,
      setLang: () => {},
      availableLocales: ["es", "pt", "en"] as Lang[],
      t: (es: string, _pt: string, _en?: string) => es,
      tk: (_key: string, fallback?: string) => fallback ?? "",
      media: (_key: string) => undefined,
    } satisfies Ctx;
  }
  return ctx;
}

/**
 * Convenience hook for media assets with a bundled fallback URL. Use this at
 * call sites that currently import a static asset — pass the imported URL as
 * the fallback so the site never renders broken if the CMS is down.
 */
export function useMedia(key: string, fallbackUrl: string, fallbackAlt = ""): {
  url: string;
  alt: string;
  kind: "image" | "video";
} {
  const { media, lang } = useT();
  const asset = media(key);
  if (!asset) return { url: fallbackUrl, alt: fallbackAlt, kind: "image" };
  const cmsUrl = asset.url && !isStaleInternalUrl(asset.url) ? asset.url : "";
  return {
    url: cmsUrl || fallbackUrl,
    alt: asset.alt?.[lang] || asset.alt?.es || fallbackAlt,
    kind: asset.kind,
  };
}

/**
 * CMS overrides sometimes point at frontend bundle paths (e.g. `/media/foo.mp4`
 * or `/assets/bar-HASH.webp`). Those URLs break when the bundle changes hash
 * or when a file is moved to the CDN, so we ignore them and fall back to the
 * bundled asset. External URLs (https://..., /__l5e/...) are kept.
 */
function isStaleInternalUrl(u: string): boolean {
  try {
    const path = new URL(u, "http://x").pathname;
    return /^\/(media|assets)\//.test(path);
  } catch {
    return false;
  }
}
